<template>
  <v-container>
    <page-skeleton v-if="loading" variant="detail" class="mt-4" />

    <template v-else>
      <v-row class="mt-6">
        <v-col cols="12">
          <class-header
            :class-data="form"
            :theme="theme"
            :members="activeMembers"
            :members-count="activeMembers.length"
            @back="goBack"
          />
        </v-col>
      </v-row>

      <!-- Card 86akng14v: convite de cadastro para turma com vaga. -->
      <v-row v-if="canInviteStudent" dense>
        <v-col cols="12" class="d-flex justify-end">
          <v-btn
            depressed
            color="#A826E1"
            class="white--text text-none"
            @click="inviteDialog = true"
          >
            <v-icon small left>mdi-account-plus-outline</v-icon>
            {{ $t("classForm.newStudent") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="pendingMembers.length" dense>
        <v-col cols="12">
          <class-requests-panel
            :requests="pendingMembers"
            :theme="theme"
            :normalized-times="normalizedTimes"
            :class-days="form.weekDays || []"
            :experimental="!!form.isExperimental"
            :trial-date="form.trialDate || null"
            :trial-scheduled-at="form.trialScheduledAt || null"
            :trial-happened="
              typeof form.trialHappened === 'boolean'
                ? form.trialHappened
                : null
            "
            @reload="loadData"
          />
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12">
          <v-card flat class="class-tabs-card">
            <v-tabs
              v-model="activeTab"
              slider-color="#A826E1"
              slider-size="3"
              class="class-tabs"
              background-color="transparent"
              show-arrows
            >
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-view-dashboard-outline</v-icon>
                {{ $t("classForm.tabOverview") }}
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-account-group-outline</v-icon>
                {{ $t("classForm.tabStudents") }}
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-clipboard-check-outline</v-icon>
                {{ $t("classForm.tabAttendance") }}
              </v-tab>
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-progress-check</v-icon>
                {{ $t("classForm.tabProgress") }}
              </v-tab>
              <!-- Card 17trt6jcyfb -->
              <v-tab class="class-tabs__tab">
                <v-icon small left>mdi-pencil-outline</v-icon>
                {{ $t("classForm.tabExercises") }}
                <!-- Retorno de 16/10: exercicio finalizado que o professor
                     ainda nao revisou. -->
                <span
                  v-if="exercisesPending"
                  class="class-tabs__dot"
                  role="img"
                  :aria-label="
                    $tc('classForm.exercisesPending', exercisesPending, {
                      count: exercisesPending,
                    })
                  "
                ></span>
              </v-tab>
            </v-tabs>

            <v-divider />

            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-overview-card
                    :class-data="form"
                    :members="activeMembers"
                    :normalized-times="normalizedTimes"
                    :theme="theme"
                    hide-hero
                    :allow-level-edit="true"
                    :level-options="levelOptions"
                    :level-busy="loadingClassLevelUpdate"
                    :allow-format-edit="true"
                    :format-options="formatOptions"
                    :format-busy="loadingClassFormatUpdate"
                    :allow-frequency-edit="true"
                    :frequency-options="frequencyOptions"
                    :frequency-busy="loadingClassFrequencyUpdate"
                    :allow-kids-edit="true"
                    :kids-busy="loadingClassKidsUpdate"
                    :members-title="$t('overview.students')"
                    :members-empty-text="$t('studentsTab.empty')"
                    @update-level="updateClassLevel"
                    @update-format="updateClassFormat"
                    @update-frequency="updateClassFrequency"
                    @update-kids="updateClassKids"
                  />
                  <class-plan-card
                    :group-id="$route.params.id"
                    :theme="theme"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-students-tab
                    :members="activeMembers"
                    :theme="theme"
                    :normalized-times="normalizedTimes"
                    :class-days="form.weekDays || []"
                    :level-options="levelOptions"
                    :partner-plans="partnerPlans"
                    @reload="loadData"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-attendance-tab
                    :group-id="$route.params.id"
                    :members="activeMembers"
                    :theme="theme"
                    :group-level="form.level"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <class-progress-tab
                    :group-id="$route.params.id"
                    :members="activeMembers"
                    :theme="theme"
                  />
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="pa-4 pa-md-6">
                  <student-exercise-overview :group-id="$route.params.id" />
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <frequency-change-dialog
      v-model="frequencyDialog"
      :plan="frequencyPlan"
      :busy="frequencyBusy"
      @confirm="confirmFrequencyChange"
    />

    <group-invite-dialog
      v-if="form.id"
      v-model="inviteDialog"
      :group-id="form.id"
      :class-code="form.cod || ''"
      :capacity="groupCapacity"
      :members-count="occupiedSeats"
    />
  </v-container>
</template>

<script>
import ClassHeader from "@/components/classes/ClassHeader.vue";
import ClassOverviewCard from "@/components/classes/ClassOverviewCard.vue";
import ClassPlanCard from "@/components/classes/ClassPlanCard.vue";
import ClassRequestsPanel from "@/components/classes/ClassRequestsPanel.vue";
import ClassStudentsTab from "@/components/classes/ClassStudentsTab.vue";
import ClassAttendanceTab from "@/components/classes/ClassAttendanceTab.vue";
import ClassProgressTab from "@/components/classes/ClassProgressTab.vue";
import StudentExerciseOverview from "@/components/exercises/StudentExerciseOverview.vue";
import partnerApi from "@/shared/pitchPartnerApi";
import FrequencyChangeDialog from "@/components/classes/FrequencyChangeDialog.vue";
import GroupInviteDialog from "@/components/classes/GroupInviteDialog.vue";
import {
  ENGLISH_LEVELS,
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
  normalizeClassTimes,
} from "@/shared/classPresentation";
import { classThemes } from "@/shared/classThemes";

export default {
  name: "PartnerClassForm",

  components: {
    FrequencyChangeDialog,
    GroupInviteDialog,
    ClassHeader,
    ClassOverviewCard,
    ClassPlanCard,
    ClassRequestsPanel,
    ClassStudentsTab,
    ClassAttendanceTab,
    ClassProgressTab,
    StudentExerciseOverview,
  },

  data() {
    return {
      loading: true,
      loadingClassLevelUpdate: false,
      loadingClassFormatUpdate: false,
      loadingClassFrequencyUpdate: false,
      frequencyDialog: false,
      frequencyPlan: null,
      frequencyBusy: false,
      pendingFrequency: null,
      loadingClassKidsUpdate: false,
      inviteDialog: false,
      activeTab: 0,
      exercisesPending: 0,
      partnerPlans: [],
      form: {
        teacher: {},
        content: [],
      },
    };
  },

  computed: {
    members() {
      return Array.isArray(this.form.content) ? this.form.content : [];
    },
    activeMembers() {
      return this.members.filter((member) => +member.status === 1);
    },
    pendingMembers() {
      return this.members.filter((member) => +member.status === 0);
    },
    // Modalidade = capacidade da turma (1 Individual .. 4 Quarteto).
    groupCapacity() {
      const capacity = +this.form.format;
      return capacity >= 1 && capacity <= 4 ? capacity : 0;
    },
    // Ativos + pedidos em aberto: a API conta os dois ao checar a vaga.
    occupiedSeats() {
      return this.activeMembers.length + this.pendingMembers.length;
    },
    // Card 86akng14v: "Vincular novo aluno" so em turma ativa com vaga.
    canInviteStudent() {
      return (
        +this.form.status === 1 &&
        !this.form.isExperimental &&
        this.occupiedSeats < this.groupCapacity
      );
    },
    normalizedTimes() {
      return normalizeClassTimes(this.form.weekDays, this.form.hours);
    },
    theme() {
      return classThemes.partner;
    },
    levelOptions() {
      return ENGLISH_LEVELS;
    },
    formatOptions() {
      return GROUP_FORMAT_OPTIONS;
    },
    frequencyOptions() {
      return GROUP_FREQUENCY_OPTIONS;
    },
  },

  async created() {
    await Promise.all([
      this.loadData(),
      this.loadPartnerPlans(),
      this.loadExercisesPending(),
    ]);
  },

  // A troca de aluno de turma (card 17trt6jda8c) leva de uma turma para outra
  // na mesma rota: o vue-router reaproveita o componente e não recria a tela.
  watch: {
    "$route.params.id"(id, previous) {
      if (id && id !== previous) {
        this.loadData();
        this.loadExercisesPending();
      }
    },
  },

  methods: {
    goBack() {
      this.$router.push({ path: "/connect/professor/turmas" });
    },
    async loadData() {
      this.loading = true;

      try {
        const res = await partnerApi.getOneStudyGroups(this.$route.params.id);
        this.form = res.data;
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: this.$t("classForm.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    // A aba so monta ao abrir; a contagem da bolinha vem separada.
    async loadExercisesPending() {
      try {
        const res = await partnerApi.getGroupExercises(this.$route.params.id);
        this.exercisesPending = (res.data?.data || []).reduce(
          (sum, student) => sum + (student.awaitingReview || 0),
          0
        );
      } catch (error) {
        this.exercisesPending = 0;
      }
    },
    async loadPartnerPlans() {
      try {
        const res = await partnerApi.getPartnerPlans();
        this.partnerPlans = res.data?.data || [];
      } catch (error) {
        this.partnerPlans = [];
      }
    },
    async updateClassLevel(payload) {
      const level = typeof payload === "object" ? payload.level : payload;
      const studentIds =
        typeof payload === "object" ? payload.studentIds || [] : [];

      if (+this.form.level === +level && studentIds.length === 0) {
        return;
      }

      this.loadingClassLevelUpdate = true;

      try {
        await partnerApi.updateStudyGroupLevel(this.$route.params.id, {
          level: +level,
          studentIds,
        });

        this.form = {
          ...this.form,
          level: +level,
        };

        this.$root.$emit("display-alert", {
          text: studentIds.length
            ? this.$t("classForm.levelAndStudents")
            : this.$t("classForm.levelUpdated"),
          type: 0,
        });

        if (studentIds.length) {
          await this.loadData();
        }
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: this.$t("classForm.levelFailed"),
          type: 1,
        });
      } finally {
        this.loadingClassLevelUpdate = false;
      }
    },
    async updateClassFormat(format) {
      if (+this.form.format === +format) return;

      this.loadingClassFormatUpdate = true;

      try {
        await partnerApi.updateStudyGroupFormat(this.$route.params.id, {
          format: +format,
        });

        this.form = { ...this.form, format: +format };

        this.$root.$emit("display-alert", {
          text: this.$t("classForm.formatUpdated"),
          type: 0,
        });
      } catch (error) {
        // A API recusa um formato que nao comporte os alunos ativos; a mensagem
        // dela e mais especifica que qualquer texto generico daqui.
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message || this.$t("classForm.formatFailed"),
          type: 1,
        });
      } finally {
        this.loadingClassFormatUpdate = false;
      }
    },
    // Card 86ak6kqa1: mudar a frequência não é gravar um número. Antes disso
    // a grade precisa comportar a nova frequência, e os eventos futuros
    // precisam ser redistribuídos — por isso o diálogo antes de aplicar.
    async updateClassFrequency(frequency) {
      if (+this.form.frequency === +frequency) return;

      this.loadingClassFrequencyUpdate = true;
      this.pendingFrequency = +frequency;

      try {
        const res = await partnerApi.getFrequencyChangePlan(
          this.$route.params.id,
          +frequency
        );
        this.frequencyPlan = res.data;
        this.frequencyDialog = true;
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message || this.$t("classForm.planFailed"),
          type: 1,
        });
      } finally {
        this.loadingClassFrequencyUpdate = false;
      }
    },
    async confirmFrequencyChange(schedules) {
      this.frequencyBusy = true;

      try {
        const res = await partnerApi.updateStudyGroupFrequency(
          this.$route.params.id,
          { frequency: this.pendingFrequency, schedules }
        );

        this.form = { ...this.form, frequency: this.pendingFrequency };
        this.frequencyDialog = false;
        this.frequencyPlan = null;

        this.$root.$emit("display-alert", {
          text: this.$tc("classForm.rescheduled", res.data?.rescheduled || 0, {
            count: res.data?.rescheduled || 0,
          }),
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message ||
            this.$t("classForm.frequencyFailed"),
          type: 1,
        });
      } finally {
        this.frequencyBusy = false;
      }
    },
    async updateClassKids(forKids) {
      if (!!this.form.forKids === !!forKids) return;

      this.loadingClassKidsUpdate = true;

      try {
        await partnerApi.updateStudyGroupKids(this.$route.params.id, {
          forKids: !!forKids,
        });

        this.form = { ...this.form, forKids: !!forKids };

        this.$root.$emit("display-alert", {
          text: forKids
            ? this.$t("classForm.kidsOn")
            : this.$t("classForm.kidsOff"),
          type: 0,
        });
      } catch (error) {
        // O switch e controlado por classData.forKids, entao ele volta sozinho
        // ao estado anterior quando a gravacao falha.
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message || this.$t("classForm.kidsFailed"),
          type: 1,
        });
      } finally {
        this.loadingClassKidsUpdate = false;
      }
    },
  },
};
</script>

<style scoped>
.class-tabs-card {
  margin-top: 16px;
  overflow: hidden;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}

.class-tabs {
  padding: 0 8px;
}

.class-tabs__tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 700;
  color: var(--dk-muted, #6b6472);
}

.class-tabs__tab.v-tab--active {
  color: var(--dk-grape, #a826e1);
}

.class-tabs__dot {
  width: 8px;
  height: 8px;
  margin: -10px 0 0 4px;
  border-radius: 50%;
  background: var(--dk-red, #e53935);
}

.class-tabs >>> .v-tabs-slider {
  border-radius: 3px;
}
</style>
